import { useState, useEffect, useRef } from "react";

// Azure CDN base URL
const AZURE_CDN_BASE = "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity";

export default function Team() {
  const [activeIndex, setActiveIndex] = useState(0); // First card on mobile, center on desktop
  const [isLgOnly, setIsLgOnly] = useState(false);
  const [isXlPlus, setIsXlPlus] = useState(false);
  const [is1xl, setIs1xl] = useState(false);
  const [is4xl, setIs4xl] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [persistentDragOffset, setPersistentDragOffset] = useState(0);
  const isDraggingRef = useRef(false);
  const touchStartRef = useRef(null);
  const lastTouchTimeRef = useRef(null);
  const lastTouchXRef = useRef(null);
  const velocityRef = useRef(0);
  const carouselContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const hasSetInitialMobileOffsetRef = useRef(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      // lg only: 1024px to 1279px (narrow), xl: 1280px-1439px, 1xl: 1440px-1919px, 4xl: 1920px+
      setIsLgOnly(width >= 1024 && width < 1280);
      setIsXlPlus(width >= 1280 && width < 1440);
      setIs1xl(width >= 1440 && width < 1920);
      setIs4xl(width >= 1920);
      setIsMobile(width < 768); // Mobile breakpoint
      // Measure carousel container width so cards can fill the space
      if (carouselContainerRef.current) {
        setContainerWidth(carouselContainerRef.current.clientWidth);
      } else {
        setContainerWidth(width);
      }
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // On mobile: set initial scroll so the first card starts at the left edge (no left padding)
  useEffect(() => {
    if (!isMobile) {
      hasSetInitialMobileOffsetRef.current = false;
      return;
    }
    if (containerWidth <= 0 || hasSetInitialMobileOffsetRef.current) return;
    const totalCards = 4;
    const gap = 8;
    const baseCardWidth = Math.floor(containerWidth * 0.78);
    const totalBaseWidth = totalCards * baseCardWidth + (totalCards - 1) * gap;
    // Position so first card's left edge is at 0 (start of container)
    const initialOffset = -containerWidth / 2 + totalBaseWidth / 2;
    setPersistentDragOffset(initialOffset);
    hasSetInitialMobileOffsetRef.current = true;
  }, [isMobile, containerWidth]);

  // Update container width when carousel ref is available (e.g. after layout)
  useEffect(() => {
    if (!carouselContainerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (carouselContainerRef.current) {
        setContainerWidth(carouselContainerRef.current.clientWidth);
      }
    });
    ro.observe(carouselContainerRef.current);
    return () => ro.disconnect();
  }, []);

  const teamMembers = [
    {
      id: 0,
      name: "Jake William Jones",
      title: "Founder and CEO, Enfinity",
      image: `${AZURE_CDN_BASE}/jake-pfp.png`,
      gradient: "from-[#fafafa] to-[#93c5fd]", // Soft pastel blue
    },
    {
      id: 1,
      name: "Theo",
      title: "Director of Growth and Operations",
      image: `${AZURE_CDN_BASE}/theodore.png`,
      gradient: "from-[#fafafa] to-[#ff8c69]", // Soft pastel orange
    },
    {
      id: 2,
      name: "Czarina",
      title: "Director of Finance",
      image: `${AZURE_CDN_BASE}/czarina.png`,
      gradient: "from-[#fafafa] to-[#86efac]", // Soft pastel green
    },
    {
      id: 3,
      name: "Cyril",
      title: "Growth Associate",
      image: `${AZURE_CDN_BASE}/chai.png`,
      gradient: "from-[#fafafa] to-[#c4b5fd]", // Soft pastel violet
    },
  ];

  // Touch handlers for mobile dragging
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    // Check mobile on each touch event
    const isMobileDevice = window.innerWidth < 768;
    if (!isMobileDevice) return;
    
    const touch = e.touches[0];
    if (touch) {
      const startX = touch.clientX;
      const now = Date.now();
      touchStartRef.current = startX;
      lastTouchXRef.current = startX;
      lastTouchTimeRef.current = now;
      velocityRef.current = 0;
      isDraggingRef.current = true;
      setTouchEnd(null);
      setTouchStart(startX);
      setIsDragging(true);
      setDragOffset(0);
    }
  };

  const onTouchMove = (e) => {
    const isMobileDevice = window.innerWidth < 768;
    if (!isMobileDevice || !isDraggingRef.current || touchStartRef.current === null) return;
    
    e.preventDefault(); // Prevent default scrolling during drag
    const touch = e.touches[0];
    if (touch) {
      const currentX = touch.clientX;
      const now = Date.now();
      const rawOffset = currentX - touchStartRef.current;
      
      // Calculate velocity for smooth momentum
      if (lastTouchTimeRef.current && lastTouchXRef.current !== null) {
        const timeDelta = now - lastTouchTimeRef.current;
        const distanceDelta = currentX - lastTouchXRef.current;
        if (timeDelta > 0) {
          // Velocity in pixels per millisecond, smoothed
          const instantVelocity = distanceDelta / timeDelta;
          velocityRef.current = velocityRef.current * 0.7 + instantVelocity * 0.3; // Smooth the velocity
        }
      }
      lastTouchXRef.current = currentX;
      lastTouchTimeRef.current = now;
      
      // Calculate constraints - mobile: card width 78% of viewport; match render logic
      const totalCards = teamMembers.length;
      const gap = 8;
      const viewportWidth = window.innerWidth;
      const baseCardWidth = Math.floor(viewportWidth * 0.78);
      const totalBaseWidth = (totalCards * baseCardWidth) + ((totalCards - 1) * gap);
      const containerWidth = viewportWidth;
      
      // Calculate the position of first and last cards when centered
      const centerOffset = -totalBaseWidth / 2 + (baseCardWidth / 2);
      const firstCardCenterX = centerOffset; // First card (index 0)
      const lastCardCenterX = centerOffset + ((totalCards - 1) * (baseCardWidth + gap)); // Last card
      
      // Calculate constraints
      // Max offset (drag right): first card's left edge should reach left edge of container
      const firstCardLeftEdge = firstCardCenterX - (baseCardWidth / 2);
      const containerLeftEdge = -containerWidth / 2;
      // Calculate how much we need to move to see the first card, then add buffer
      // When dragging right (positive offset), cards move right, revealing left cards
      // If first card is to the left of container, we need positive offset to bring it into view
      const distanceToFirstCard = containerLeftEdge - firstCardLeftEdge;
      const firstCardVisibilityBuffer = 100; // Buffer to ensure first card is fully visible
      // Allow scrolling to bring first card into view
      // Ensure maxOffset is large enough - use the larger of calculated value or a minimum threshold
      const calculatedMaxOffset = distanceToFirstCard + firstCardVisibilityBuffer;
      const maxOffset = Math.max(calculatedMaxOffset, 100); // Ensure at least 400px of scrolling is allowed
      
      // Min offset (drag left): last card's right edge should reach right edge of container
      // Account for full card width to ensure last card is fully visible
      const lastCardRightEdge = lastCardCenterX + (baseCardWidth / 2);
      const containerRightEdge = containerWidth / 2;
      // Allow last card to be fully visible
      // Calculate how much we need to move to see the last card, then subtract buffer
      const distanceToLastCard = containerRightEdge - lastCardRightEdge;
      const visibilityBuffer = 10; // Minimal buffer to ensure last card edge is visible
      // Ensure minOffset allows enough scrolling - use the smaller (more negative) of calculated value or threshold
      // Negative values allow left scrolling to reveal the last card
      const calculatedMinOffset = distanceToLastCard - visibilityBuffer;
      const minOffset = Math.min(calculatedMinOffset, -230); // Ensure at least 400px of left scrolling is allowed (negative for left)
      
      // Constrain the total offset (persistent + current drag)
      const totalOffset = persistentDragOffset + rawOffset;
      const constrainedTotalOffset = Math.max(minOffset, Math.min(maxOffset, totalOffset));
      const constrainedDragOffset = constrainedTotalOffset - persistentDragOffset;
      
      setDragOffset(constrainedDragOffset);
      setTouchEnd(currentX);
    }
  };

  const onTouchEnd = (e) => {
    const isMobileDevice = window.innerWidth < 768;
    if (!isMobileDevice) {
      isDraggingRef.current = false;
      setIsDragging(false);
      return;
    }
    
    // Calculate constraints (same as in onTouchMove)
    const totalCards = teamMembers.length;
    const gap = 8;
    const viewportWidth = window.innerWidth;
    const baseCardWidth = Math.floor(viewportWidth * 0.78);
    const totalBaseWidth = (totalCards * baseCardWidth) + ((totalCards - 1) * gap);
    const containerWidth = viewportWidth;
    
    // Calculate the position of first and last cards when centered
    const centerOffset = -totalBaseWidth / 2 + (baseCardWidth / 2);
    const firstCardCenterX = centerOffset; // First card (index 0)
    const lastCardCenterX = centerOffset + ((totalCards - 1) * (baseCardWidth + gap)); // Last card
    
    // Calculate constraints
    const firstCardLeftEdge = firstCardCenterX - (baseCardWidth / 2);
    const containerLeftEdge = -containerWidth / 2;
    // Calculate how much we need to move to see the first card, then add buffer
    // When dragging right (positive offset), cards move right, revealing left cards
    // If first card is to the left of container, we need positive offset to bring it into view
    const distanceToFirstCard = containerLeftEdge - firstCardLeftEdge;
    const firstCardVisibilityBuffer = 400; // Buffer to ensure first card is fully visible
    // Allow scrolling to bring first card into view
    // Ensure maxOffset is large enough - use the larger of calculated value or a minimum threshold
    const calculatedMaxOffset = distanceToFirstCard + firstCardVisibilityBuffer;
    const maxPersistentOffset = Math.max(calculatedMaxOffset, 400); // Ensure at least 400px of scrolling is allowed
    
    const lastCardRightEdge = lastCardCenterX + (baseCardWidth / 2);
    const containerRightEdge = containerWidth / 2;
    // Allow last card to be fully visible
    // Calculate how much we need to move to see the last card, then subtract buffer
    const distanceToLastCard = containerRightEdge - lastCardRightEdge;
    const visibilityBuffer = 10; // Minimal buffer to ensure last card edge is visible
    // Ensure minOffset allows enough scrolling - use the smaller (more negative) of calculated value or threshold
    // Negative values allow left scrolling to reveal the last card
    const calculatedMinOffset = distanceToLastCard - visibilityBuffer;
    const minPersistentOffset = Math.min(calculatedMinOffset, -400); // Ensure at least 400px of left scrolling is allowed (negative for left)
    
    // Persist the drag offset, but constrain it (no momentum to prevent bouncing)
    const newPersistentOffset = persistentDragOffset + dragOffset;
    const constrainedOffset = Math.max(minPersistentOffset, Math.min(maxPersistentOffset, newPersistentOffset));
    
    // Set the offset directly - CSS transition will handle smoothness without bounce
    setIsDragging(false);
    setDragOffset(0);
    setPersistentDragOffset(constrainedOffset);
    
    // Check for swipe gesture
    if (touchStartRef.current !== null && touchEnd !== null) {
      const distance = touchStartRef.current - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;

      if (isLeftSwipe && activeIndex < teamMembers.length - 1) {
        setActiveIndex(prev => prev + 1);
      } else if (isRightSwipe && activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      }
    }
    
    isDraggingRef.current = false;
    setTouchStart(null);
    setTouchEnd(null);
    touchStartRef.current = null;
    lastTouchXRef.current = null;
    lastTouchTimeRef.current = null;
    velocityRef.current = 0;
  };

  return (
    <div className="bg-white relative py-12 md:py-16 lg:py-20 pl-0 pr-1 md:px-8 lg:px-12 xl:px-16 4xl:px-16">
      {/* Team Section Container */}
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-12 md:mb-16 lg:mb-20 flex flex-col lg:flex-row gap-8 lg:gap-0 items-start">
          {/* Heading */}
          <h1 className="text-4xl md:text-5xl xxs:text-[55px] lg:text-[60px] xl:text-[80px] 4xl:text-[90px] font-normal enfinity-header-font leading-tight xxs:leading-tight order-1 lg:order-1 xxs:px-8 lg:px-4 xl:px-0 4xl:px-0">
            <span className="bg-gradient-to-r from-green-500 to-blue-400 bg-clip-text text-transparent">
              Leadership
            </span>{" "}
            <span className="text-black">shaping the Creator Economy</span>
          </h1>
          
          {/* Description */}
          <p className="text-lg md:text-xl xxs:text-[1rem] lg:text-xl text-gray-700 enfinity-subtext-font leading-relaxed order-2 lg:order-2 text-left lg:text-right lg:ml-auto max-w-sm mt-auto xxs:px-8 lg:px-4 xl:px-0 4xl:px-0">
            We are a team of creators, operators, and engineers focused on helping you grow faster, earn more, and stay in control, with tools and support built for real content workflows.
          </p>
        </div>

        {/* Simple Horizontal Carousel - cards start at left edge; on mobile pull left to cancel any parent padding */}
        <div className="max-md:-ml-8 max-md:w-[calc(100%+2rem)] md:ml-0 md:w-full">
        <div 
          ref={carouselContainerRef}
          className="relative w-full flex items-end justify-center overflow-hidden max-md:touch-none md:h-[600px] lg:h-[550px]"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            ...(isMobile && { height: Math.floor((containerWidth || 375) * 0.78 * 1.2) }),
            touchAction: isMobile ? 'none' : 'auto',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            overscrollBehavior: 'none',
            overscrollBehaviorX: 'none',
          }}
        >
          {/* Carousel Items */}
          <div 
            className="flex items-center justify-center w-full max-w-full md:max-w-2xl lg:max-w-xl xl:max-w-xl 2xl:max-w-6xl mx-auto relative max-md:touch-none"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {teamMembers.map((member, index) => {
              const isCenter = index === activeIndex;
              const distanceFromCenter = Math.abs(index - activeIndex);
              
              // Mobile: one card centered with neighbors peeking; Desktop: cards fill width
              const totalCards = teamMembers.length;
              const gap = is4xl ? 12 : is1xl ? 10 : isXlPlus ? 10 : (isMobile ? 8 : 8);
              const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;

              let baseCardWidth;
              if (isMobileDevice) {
                // Mobile: card width 78% of viewport for margin
                const mobileWidth = containerWidth || 375;
                baseCardWidth = Math.floor(mobileWidth * 0.78);
              } else {
                const minCardWidth = 80;
                const computedWidth = containerWidth > 0
                  ? (containerWidth - (totalCards - 1) * gap) / totalCards
                  : (is4xl ? 145 : is1xl ? 145 : isXlPlus ? 126 : 100);
                baseCardWidth = Math.max(minCardWidth, computedWidth);
              }

              const totalBaseWidth = (totalCards * baseCardWidth) + ((totalCards - 1) * gap);
              const centerOffset = -totalBaseWidth / 2 + (baseCardWidth / 2);
              const baseX = centerOffset + (index * (baseCardWidth + gap));

              const finalX = isMobileDevice ? baseX + persistentDragOffset + (isDragging ? dragOffset : 0) : baseX;

              // Slightly fade non-center cards on mobile for emphasis
              const opacity = isMobileDevice && !isCenter ? 0.92 : 1;

              return (
                <div
                  key={member.id}
                  className={`absolute flex flex-col items-center ${isDragging ? '' : 'transition-transform duration-300 ease-out'}`}
                  style={{
                    transform: `translateX(${finalX}px)`,
                    opacity: Math.max(opacity, 0.5),
                    left: "50%",
                    bottom: isMobileDevice ? "0" : "48px",
                    marginLeft: `-${baseCardWidth / 2}px`, // Responsive left margin
                    zIndex: isCenter ? 40 : teamMembers.length - distanceFromCenter,
                    cursor: 'default',
                    touchAction: 'none',
                    ...(isDragging ? {} : {
                      transition: 'transform 0.3s ease-out',
                    }),
                  }}
                >
                  {/* Portrait Panel - taller than wide on mobile, fixed height on desktop */}
                  <div
                    className={`bg-gradient-to-b ${member.gradient} overflow-hidden relative flex flex-col items-center justify-start rounded-2xl flex-shrink-0`}
                    style={{
                      width: `${baseCardWidth}px`,
                      minWidth: `${baseCardWidth}px`,
                      height: isMobileDevice ? `${Math.floor(baseCardWidth * 1.2)}px` : (is4xl ? "500px" : is1xl ? "490px" : isXlPlus ? "480px" : "400px"),
                      minHeight: isMobileDevice ? `${Math.floor(baseCardWidth * 1.2)}px` : undefined,
                    }}
                  >
                    {/* Name and Title - always visible */}
                    <div className="absolute top-6 left-6 right-5 z-10 text-left">
                      <div className="text-2xl md:text-3xl lg:text-[25px] font-normal text-black mb-1.5 leading-tight enfinity-header-font">
                        {member.name}
                      </div>
                      <p className="text-base md:text-lg lg:text-[15px] 4xl:text-[16px] font-normal text-black opacity-95 enfinity-subtext-font">
                        {member.title}
                      </p>
                    </div>

                    {/* Image - Cyril's container (full color, no grayscale) */}
                    {member.id === 3 && (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="absolute w-full object-cover"
                        style={{
                          ...(isMobileDevice ? { top: "20%", bottom: "auto" } : { bottom: 0 }),
                          height: isMobileDevice ? "100%" : "80%",
                          objectPosition: "55% bottom",
                          left: "0",
                          right: "0",
                        }}
                        draggable={false}
                      />
                    )}

                    {/* Image - Jake William Jones (id 0, full color) */}
                    {member.id === 0 && (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="absolute w-full object-cover"
                        style={{
                          ...(isMobileDevice ? { top: "25%", bottom: "auto" } : { bottom: -80 }),
                          height: isMobileDevice ? "100%" : "90%",
                          objectPosition: isMobileDevice ? "10% 35%" : "55% bottom",
                          left: "0",
                          right: "0",
                        }}
                        draggable={false}
                      />
                    )}

                    {/* Image - Theo (id 1, full color) */}
                    {member.id === 1 && (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="absolute w-full object-cover"
                        style={{
                          ...(isMobileDevice ? { top: "20%", bottom: "auto" } : { bottom: 0 }),
                          height: isMobileDevice ? "100%" : "80%",
                          objectPosition: "40% bottom",
                          left: "0",
                          right: "0",
                        }}
                        draggable={false}
                      />
                    )}

                    {/* Image - Czarina (id 2, full color) */}
                    {member.id === 2 && (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="absolute w-full object-cover"
                        style={{
                          ...(isMobileDevice ? { top: "25%", bottom: "auto" } : { bottom: -80 }),
                          height: isMobileDevice ? "110%" : "90%",
                          objectPosition: isMobileDevice ? "10% 35%" : "70% bottom",
                          left: "0",
                          right: "0",
                        }}
                        draggable={false}
                      />
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </div>

        {/* Employee count - below the containers */}
        <p className="text-center text-black font-normal lg:mt-2 xxs:mt-8 md:mt-10 enfinity-header-font text-xl xxs:text-[1rem] md:text-2xl">
          +60 amazing employees
        </p>
      </div>
    </div>
  );
}
